// Línea de tiempo de seguimientos de una conversación.
//
// El backend nuevo manda `timeline` ({ cadence, items }); el viejo solo
// {pending, done}. Todo lo que dibuja el panel pasa por acá para que haya UN
// solo camino de render: si no viene `timeline`, se arma uno equivalente con
// los pending/done de siempre (sin cadencia).
//
// Sin clases de Tailwind acá: Tailwind solo escanea .vue/.erb.
import {
  addDays,
  differenceInCalendarDays,
  format,
  isSameDay,
  isValid,
  parseISO,
} from 'date-fns';
import es from 'date-fns/locale/es';

export const SOURCES = ['automatic', 'proactive', 'personalized', 'recontact'];
export const STATUSES = [
  'sent',
  'skipped',
  'failed',
  'scheduled',
  'projected',
  'canceled',
  'pending_approval',
];

// El pendiente del seguimiento automático viejo (id "trad-pending-...") NO se
// puede cancelar con el DELETE de proactivos: el backend responde 500.
const isAutomaticLegacyId = id => String(id || '').startsWith('trad-');

const toDate = value => {
  if (!value) return null;
  const date = value instanceof Date ? value : parseISO(String(value));
  return isValid(date) ? date : null;
};

const toPositiveInt = value => {
  const number = Number(value);
  return Number.isInteger(number) && number > 0 ? number : null;
};

// ISO válido o null (el backend viejo no manda created_at).
const isoOrNull = value => (toDate(value) ? String(value) : null);

const textOrNull = value =>
  typeof value === 'string' && value.trim() ? value.trim() : null;

export const normalizeItem = (raw, index = 0) => {
  if (!raw || typeof raw !== 'object') return null;
  const source = SOURCES.includes(raw.source) ? raw.source : 'proactive';
  const status = STATUSES.includes(raw.status) ? raw.status : 'scheduled';
  const createdAt = isoOrNull(raw.created_at);
  const scheduledAt = isoOrNull(raw.scheduled_at);
  const sentAt = isoOrNull(raw.sent_at);
  const at = raw.at || sentAt || scheduledAt || createdAt || null;
  const cancelable = Boolean(raw.cancelable) && Boolean(raw.cancel_id);
  return {
    id: String(raw.id || `${source}-${status}-${index}`),
    source,
    status,
    step: source === 'automatic' ? toPositiveInt(raw.step) : null,
    totalSteps: source === 'automatic' ? toPositiveInt(raw.total_steps) : null,
    title: textOrNull(raw.title),
    detail: textOrNull(raw.detail),
    messageText: textOrNull(raw.message_text),
    messageId: toPositiveInt(raw.message_id),
    at,
    date: toDate(at),
    createdAt,
    createdDate: toDate(createdAt),
    scheduledAt,
    scheduledDate: toDate(scheduledAt),
    sentAt,
    sentDate: toDate(sentAt),
    estimated: Boolean(raw.estimated) || status === 'projected',
    cancelable,
    cancelId: cancelable ? String(raw.cancel_id) : null,
  };
};

// Orden ascendente por fecha; lo que no tiene fecha va al final sin perder el
// orden en que vino.
export const sortItems = items =>
  items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const ta = a.item.date ? a.item.date.getTime() : Infinity;
      const tb = b.item.date ? b.item.date.getTime() : Infinity;
      if (ta !== tb) return ta - tb;
      return a.index - b.index;
    })
    .map(({ item }) => item);

export const normalizeCadence = raw => {
  if (!raw || typeof raw !== 'object') return null;
  const totalSteps = toPositiveInt(raw.total_steps);
  const sentSteps = Math.max(0, Number(raw.sent_steps) || 0);
  return {
    enabled: Boolean(raw.enabled),
    active: Boolean(raw.active),
    disabled: Boolean(raw.disabled),
    exhausted: Boolean(raw.exhausted),
    sentSteps,
    totalSteps,
    nextStep: toPositiveInt(raw.next_step),
    nextAt: toDate(raw.next_at) ? raw.next_at : null,
  };
};

const LEGACY_STATUS = {
  scheduled: 'scheduled',
  draft: 'scheduled',
  sent: 'sent',
  failed: 'failed',
  canceled: 'canceled',
};

// Backend viejo: arma items a partir de pending/done.
export const legacyTimeline = ({ pending = [], done = [] } = {}) => {
  const items = [...pending, ...done]
    .filter(entry => entry && typeof entry === 'object')
    .map((entry, index) => {
      const automatic = isAutomaticLegacyId(entry.id);
      const status = LEGACY_STATUS[entry.status] || 'scheduled';
      return normalizeItem(
        {
          id: entry.id,
          source: automatic ? 'automatic' : 'proactive',
          status,
          title: entry.entry_name,
          detail: entry.message_goal,
          message_text: entry.message_text || entry.message,
          message_id: entry.message_id,
          created_at: entry.created_at,
          scheduled_at: entry.scheduled_at,
          sent_at: entry.sent_at,
          at: entry.sent_at || entry.scheduled_at || entry.created_at,
          cancelable: !automatic && status === 'scheduled',
          cancel_id: entry.id,
        },
        index
      );
    })
    .filter(Boolean);
  return { cadence: null, items: sortItems(items), legacy: true };
};

// Punto de entrada del store: siempre devuelve { cadence, items, legacy }.
export const buildTimeline = data => {
  const payload = data || {};
  const timeline = payload.timeline;
  if (!timeline || typeof timeline !== 'object') {
    return legacyTimeline(payload);
  }
  const items = (Array.isArray(timeline.items) ? timeline.items : [])
    .map(normalizeItem)
    .filter(Boolean);
  return {
    cadence: normalizeCadence(timeline.cadence),
    items: sortItems(items),
    legacy: false,
  };
};

// ---- Fechas -----------------------------------------------------------------

const dateLocale = locale =>
  String(locale || '').startsWith('es') ? es : undefined;

// Clave del día para agrupar ("hoy", "mañana", "ayer" o la fecha corta).
export const dayKey = date => (date ? format(date, 'yyyy-MM-dd') : 'none');

// { kind: 'today' | 'tomorrow' | 'yesterday' | 'date' | 'none', label }
export const relativeDay = (date, { now = new Date(), locale } = {}) => {
  if (!date) return { kind: 'none', label: '' };
  if (isSameDay(date, now)) return { kind: 'today', label: '' };
  if (isSameDay(date, addDays(now, 1))) return { kind: 'tomorrow', label: '' };
  if (isSameDay(date, addDays(now, -1)))
    return { kind: 'yesterday', label: '' };
  const pattern =
    date.getFullYear() === now.getFullYear() ? 'EEE d MMM' : 'd MMM yyyy';
  return {
    kind: 'date',
    label: format(date, pattern, { locale: dateLocale(locale) }),
  };
};

export const timeOfDay = date => (date ? format(date, 'HH:mm') : '');

export const absoluteDate = (date, locale) =>
  date ? format(date, 'PPPp', { locale: dateLocale(locale) }) : '';

// Fecha + hora corta en la zona del navegador: "mié 24 sep, 10:32" (con el
// año solo si no es el actual).
export const shortDateTime = (date, { now = new Date(), locale } = {}) => {
  if (!date) return '';
  const pattern =
    date.getFullYear() === now.getFullYear()
      ? 'EEE d MMM, HH:mm'
      : 'EEE d MMM yyyy, HH:mm';
  return format(date, pattern, { locale: dateLocale(locale) });
};

const MINUTE_MS = 60 * 1000;

// Duración legible: { unit: 'minutes' | 'hours' | 'days', value }.
export const durationParts = minutes => {
  const abs = Math.max(0, Math.round(Math.abs(minutes)));
  if (abs < 60) return { unit: 'minutes', value: Math.max(1, abs) };
  if (abs < 48 * 60) return { unit: 'hours', value: Math.round(abs / 60) };
  return { unit: 'days', value: Math.round(abs / (24 * 60)) };
};

// Demora del envío respecto de lo programado, en minutos. Null si no hay
// alguna de las dos fechas o si salió dentro del margen (5 min).
export const DELAY_THRESHOLD_MINUTES = 5;
export const sendDelayMinutes = item => {
  if (!item || !item.sentDate || !item.scheduledDate) return null;
  const minutes =
    (item.sentDate.getTime() - item.scheduledDate.getTime()) / MINUTE_MS;
  return minutes > DELAY_THRESHOLD_MINUTES ? minutes : null;
};

// Cuenta regresiva hasta `date`:
//   { kind: 'now' }                       vence en menos de 1 min (o recién venció)
//   { kind: 'overdue', unit, value }      venció hace más de 15 min
//   { kind: 'in', unit, value }           "en 3 h", "en 20 min", "en 2 días"
//   { kind: 'tomorrow', time }            "mañana 10:00" (si falta bastante)
export const countdown = (date, { now = new Date() } = {}) => {
  if (!date) return null;
  const minutes = (date.getTime() - now.getTime()) / MINUTE_MS;
  if (minutes < -15) return { kind: 'overdue', ...durationParts(minutes) };
  if (minutes < 1) return { kind: 'now' };
  if (minutes < 60) {
    return { kind: 'in', unit: 'minutes', value: Math.ceil(minutes) };
  }
  if (isSameDay(date, addDays(now, 1)) && minutes >= 6 * 60) {
    return { kind: 'tomorrow', time: timeOfDay(date) };
  }
  if (minutes < 24 * 60) {
    return { kind: 'in', unit: 'hours', value: Math.round(minutes / 60) };
  }
  return {
    kind: 'in',
    unit: 'days',
    value: Math.max(1, differenceInCalendarDays(date, now)),
  };
};

// Agrupa items consecutivos del mismo día (ya vienen ordenados).
export const groupByDay = items =>
  items.reduce((groups, item) => {
    const key = dayKey(item.date);
    const last = groups[groups.length - 1];
    if (last && last.key === key) {
      last.items.push(item);
    } else {
      groups.push({ key, date: item.date, items: [item] });
    }
    return groups;
  }, []);
